import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDataProvider } from 'react-admin';

interface TimezoneContextValue {
  timezone: string;
  isLoading: boolean;
  error: Error | null;
  formatDate: (date: Date | string, format?: 'date' | 'time' | 'datetime') => string;
  getStartOfDay: (date?: Date) => Date;
  getEndOfDay: (date?: Date) => Date;
  convertToTimezone: (date: Date | string) => Date;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

// Default timezone if not set
const DEFAULT_TIMEZONE = 'Asia/Hong_Kong';

export const TimezoneProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timezone, setTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const dataProvider = useDataProvider();

  useEffect(() => {
    // Fetch system timezone from settings
    const fetchTimezone = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // First try to get from public settings (no auth required)
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/api/public/settings`);
          if (response.ok) {
            const data = await response.json();
            if (data.settings?.system_timezone) {
              setTimezone(data.settings.system_timezone);
              setIsLoading(false);
              return;
            }
          }
        } catch (publicError) {
          console.warn('Failed to fetch public settings:', publicError);
        }
        
        // If public settings fail, try authenticated endpoint
        try {
          const { data } = await dataProvider.getOne('settings', { id: 'system_timezone' });
          if (data?.value) {
            setTimezone(data.value);
          }
        } catch (authError) {
          console.warn('Failed to fetch authenticated settings:', authError);
        }
      } catch (err) {
        console.error('Error fetching timezone:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimezone();
  }, [dataProvider]);

  // Format a date in the current timezone
  const formatDate = (date: Date | string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
    };
    
    switch (format) {
      case 'date':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case 'time':
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = false;
        break;
      case 'datetime':
      default:
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = false;
        break;
    }
    
    return new Intl.DateTimeFormat('en-GB', options).format(dateObj);
  };

  // Get start of day in the current timezone
  const getStartOfDay = (date?: Date): Date => {
    const targetDate = date || new Date();
    
    // Format the date in the target timezone to get local date components
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    const parts = formatter.formatToParts(targetDate);
    const dateMap = new Map<string, string>();
    
    parts.forEach(part => {
      if (part.type !== 'literal') {
        dateMap.set(part.type, part.value);
      }
    });
    
    const year = parseInt(dateMap.get('year') || '2024');
    const month = parseInt(dateMap.get('month') || '1');
    const day = parseInt(dateMap.get('day') || '1');
    
    // Create a date string in the target timezone
    const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00`;
    
    // Use the timezone-aware constructor
    const startOfDay = new Date(dateStr);
    
    // Adjust for timezone offset
    const tzOffset = getTimezoneOffset(timezone, targetDate);
    startOfDay.setMinutes(startOfDay.getMinutes() - tzOffset);
    
    return startOfDay;
  };

  // Get end of day in the current timezone
  const getEndOfDay = (date?: Date): Date => {
    const targetDate = date || new Date();
    
    // Format the date in the target timezone to get local date components
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    const parts = formatter.formatToParts(targetDate);
    const dateMap = new Map<string, string>();
    
    parts.forEach(part => {
      if (part.type !== 'literal') {
        dateMap.set(part.type, part.value);
      }
    });
    
    const year = parseInt(dateMap.get('year') || '2024');
    const month = parseInt(dateMap.get('month') || '1');
    const day = parseInt(dateMap.get('day') || '1');
    
    // Create a date string in the target timezone
    const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T23:59:59.999`;
    
    // Use the timezone-aware constructor
    const endOfDay = new Date(dateStr);
    
    // Adjust for timezone offset
    const tzOffset = getTimezoneOffset(timezone, targetDate);
    endOfDay.setMinutes(endOfDay.getMinutes() - tzOffset);
    
    return endOfDay;
  };

  // Convert a date to the current timezone
  const convertToTimezone = (date: Date | string): Date => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj; // JavaScript Date objects are timezone-agnostic
  };

  // Get timezone offset in minutes
  const getTimezoneOffset = (tz: string, date: Date): number => {
    // Create a date string in ISO format
    const isoString = date.toISOString();
    
    // Format it in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    const parts = formatter.formatToParts(date);
    const dateMap = new Map<string, string>();
    
    parts.forEach(part => {
      if (part.type !== 'literal') {
        dateMap.set(part.type, part.value);
      }
    });
    
    // Reconstruct the date in the target timezone
    const year = parseInt(dateMap.get('year') || '2024');
    const month = parseInt(dateMap.get('month') || '1') - 1; // JavaScript months are 0-indexed
    const day = parseInt(dateMap.get('day') || '1');
    const hour = parseInt(dateMap.get('hour') || '0');
    const minute = parseInt(dateMap.get('minute') || '0');
    const second = parseInt(dateMap.get('second') || '0');
    
    const tzDate = new Date(year, month, day, hour, minute, second);
    
    // Calculate offset in minutes
    return (date.getTime() - tzDate.getTime()) / (1000 * 60);
  };

  const value: TimezoneContextValue = {
    timezone,
    isLoading,
    error,
    formatDate,
    getStartOfDay,
    getEndOfDay,
    convertToTimezone,
  };

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

export const useTimezone = (): TimezoneContextValue => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};